import { ReduxStore } from '../redux-store/ReduxStore';
import { auth } from '../authentication/Firebase.jsx';

/**
 * Auth + tenant headers for fetch()-based SSE calls.
 *
 * Consuming server-sent events needs a ReadableStream, which axios cannot hand back — so streaming
 * calls go out through fetch() and never touch the axios request interceptor. That interceptor
 * ({@link ../config/axiosConfig.js}) is where every other request in the app picks up its auth token
 * and tenant scope, so without this a streamed call would reach the backend unauthenticated and
 * unscoped.
 *
 * This mirrors the interceptor deliberately rather than sharing code with it: the interceptor
 * mutates an axios config object, this returns a plain header bag. Keep the two in step — a header
 * added there needs adding here.
 */

/**
 * Token for the Firebase rehydration window (hard refresh), when {@code auth.currentUser} is
 * briefly null while on-mount fetches are already firing. AuthContext persists the accessToken
 * under the {@code authUser} sessionStorage key; the axios interceptor reads the same fallback.
 */
const AUTH_RESTORE_WAIT_MS = 5000;
const waitForAuthRestore = () => (typeof auth.authStateReady === 'function'
    ? Promise.race([auth.authStateReady(), new Promise((resolve) => setTimeout(resolve, AUTH_RESTORE_WAIT_MS))]).catch(() => {})
    : Promise.resolve());

const readPersistedAuthToken = () => {
    try {
        const raw = sessionStorage.getItem('authUser');
        return raw ? (JSON.parse(raw)?.accessToken || null) : null;
    } catch {
        return null;
    }
};

/**
 * Build the headers for one streaming request.
 *
 * @param gameId  Scope this call to a specific game. A page addressed by URL slug must pass it —
 *                otherwise the call lands on whatever game the Redux context currently holds, which
 *                is not necessarily the one on screen.
 * @param accept  Response media type; SSE endpoints want text/event-stream.
 */
export const buildSseHeaders = async ({ gameId = null, accept = 'text/event-stream' } = {}) => {
    const headers = { 'Content-Type': 'application/json', Accept: accept };

    const ctx = ReduxStore.getState()?.dashboardContext;
    if (ctx?.selectedOrgId) headers['X-Web-Org-Id'] = ctx.selectedOrgId;
    // Explicit game wins over the active one, matching the interceptor's precedence.
    const resolvedGameId = gameId || ctx?.selectedGameId;
    if (resolvedGameId) headers['X-Web-Game-Id'] = resolvedGameId;
    // Geography scope (NOT the player segment being authored — this is the tenant's geo scope).
    if (ctx?.selectedSegmentId) headers['X-Web-Segment-Id'] = ctx.selectedSegmentId;

    // Playground swaps in a mock adapter and never hits the network; attaching a token there would
    // force a Firebase call the page does not need.
    const isPlayground = sessionStorage.getItem('playgroundMode') === 'true';
    if (!isPlayground) {
        try {
            // As the axios interceptor: after a hard refresh, wait (bounded) for Firebase to restore the session
            // rather than send the persisted token, which may be long expired.
            if (!auth.currentUser) await waitForAuthRestore();
            if (auth.currentUser) {
                headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
            } else {
                const persisted = readPersistedAuthToken();
                if (persisted) headers.Authorization = `Bearer ${persisted}`;
            }
        } catch (err) {
            // Proceed with whatever we have; the backend answers 401 and the caller surfaces it.
            console.error('buildSseHeaders: failed to attach auth token', err);
        }
    }

    // Streaming calls are POSTs, so they carry the same dedupe key a normal mutating request would.
    headers['X-Idempotency-Key'] = crypto.randomUUID();
    return headers;
};
